/*******************************************************************************
 * Copyright (c) 2024 Malond Limited. All rights reserved.
 * The source code is the property of Malond Limited.
 * The source code may not be shared with third parties without the express consent of Malond Limited.
 * THE CONTENTS OF THIS PROJECT ARE PROPRIETARY AND CONFIDENTIAL.
 * UNAUTHORIZED COPYING, TRANSFERRING OR REPRODUCTION OF THE CONTENTS OF THIS PROJECT,
 * VIA ANY MEDIUM IS STRICTLY PROHIBITED.
 * The receipt or possession of the source code and/or any parts thereof does not convey or imply
 * any right to use them for any purpose other than the purpose for which they were provided to you.
 * The source code is provided as is.
 * Any changes made to the files must be tracked.
 * Malond Limited does not claim ownership of any changes made to the code.
 ******************************************************************************/

export function convertNumberToString(number: number | string): string {
  return `${number}`
}

/**
 * Converts localized decimal string (with commas, spaces, etc.) to standard decimal string with dot for API compatibility.
 * Examples:
 * "0,95" -> "0.95"
 * "1 250,50" -> "1250.50"
 * "0" -> "0"
 * "" -> "0"
 * null/undefined -> "0"
 */
export function normalizeDecimalString(
  value?: string | number | null,
  fallback = "0"
): string {
  if (value === null || value === undefined) {
    return fallback
  }
  if (typeof value === "number") {
    if (Number.isNaN(value)) return fallback
    return `${value}`
  }
  const str = String(value).trim().replace(/\s+/g, "").replace(",", ".")
  if (str === "") {
    return fallback
  }
  return str
}

/**
 * Checks if a string represents a valid decimal or integer number (allows both '.' and ',')
 */
export function isValidDecimalString(value?: string | number | null): boolean {
  if (value === null || value === undefined) return false
  const str = String(value).trim().replace(/\s+/g, "").replace(",", ".")
  if (str === "") return false
  const num = Number(str)
  return !Number.isNaN(num) && Number.isFinite(num)
}

/**
 * Formats a number with thousands separators (ru-RU locale, space separator).
 */
export function formatNumber(
  value?: number | string | null,
  options?: { minimumFractionDigits?: number; maximumFractionDigits?: number }
): string {
  if (value === null || value === undefined || value === "") return "0"
  const normalized = normalizeDecimalString(value, "0")
  const num = Number(normalized)
  if (Number.isNaN(num)) return "0"

  return new Intl.NumberFormat("ru-RU", {
    minimumFractionDigits: options?.minimumFractionDigits ?? 0,
    maximumFractionDigits: options?.maximumFractionDigits ?? 2,
  })
    .format(num)
    .replace(/\u00A0/g, " ")
}

/**
 * Formats a currency value in Kazakhstani Tenge (₸).
 */
export function formatPriceKZT(value?: number | string | null): string {
  return `${formatNumber(value)} ₸`
}

/**
 * Formats a percentage value (e.g. 15 -> "15 %").
 */
export function formatPercent(value?: number | string | null): string {
  if (value === null || value === undefined || value === "") return "0 %"
  const normalized = normalizeDecimalString(value, "0")
  const num = Number(normalized)
  if (Number.isNaN(num)) return "0 %"
  return `${formatNumber(num)} %`
}
